import FormUnit from "@/components/FormUnit";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { FC } from "react";
import {
  getMaxLengthMessage,
  getProAccountMessage,
  RequiredInputMessage,
  RequiredSelectMessage,
} from "@/domain/values/InputErrorMessage";
import { DesignerAccountUpdateParam } from "@/web-client/api";
import Prefectures from "@/domain/values/Prefectures";
import SingleSelect from "@/components/SingleSelect";
import useUserAttributes from "@/features/user/hooks/useUserAttributes";
import useQualifications from "@/hooks/useQualifications";
import CheckboxWithLabel from "@/components/CheckboxWithLabel";
import Maybe from "@/components/Maybe";
import { MultiColumnInputBox } from "@/components/MultiColumnInputBox";
import useMyUser from "@/features/user/hooks/useMyUser";
import Button from "@/components/Button";
import useDevice from "@/hooks/useDevice";
import useBirthYearOptions from "@/hooks/useBirthYearOptions";
import { useCheckJobForProAccount } from "@/features/user_registration/hooks/useCheckJobForNewProAccount";
import {
  MaxLengthOfDepartment,
  MaxLengthOfCompanyName,
  MaxLengthOfUserName,
  MaxLengthOfBranch,
  MaxLengthOfOfficialPosition,
} from "@/domain/values/MaxLengths";

type Props = {
  onSubmit: SubmitHandler<DesignerAccountUpdateParam>;
};

const ProAccountForm: FC<Props> = ({ onSubmit }) => {
  const { isSp } = useDevice();
  const { user } = useMyUser();

  const form = useForm<DesignerAccountUpdateParam>({
    defaultValues: {
      first_name: user.designer?.first_name,
      last_name: user.designer?.last_name,
      industry_id: user.industry?.id,
      occupation_id: user.occupation?.id,
      company_name: user.designer?.company_name,
      company_prefecture_id: user.designer?.company_prefecture_id,
      department: user.designer?.department,
      branch: user.designer?.branch,
      official_position: user.designer?.official_position,
      birth_year: user.designer?.birth_year,
      qualification_ids: user.designer?.qualifications.map((q) => q.id) || [],
    },
  });
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = form;

  const { recommendDesigner } = useCheckJobForProAccount(
    watch("industry_id"),
    watch("occupation_id"),
  );

  const { industries, occupations } = useUserAttributes();
  const qualifications = useQualifications();
  const birthYearOptions = useBirthYearOptions();

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid gap-40">
          <div className="grid gap-24">
            <MultiColumnInputBox className="w-full">
              <FormUnit
                id="last_name"
                label="姓"
                error={errors.last_name?.message || ""}
                attention="strongRequired"
              >
                <input
                  type="text"
                  {...register("last_name", {
                    required: RequiredInputMessage,
                    maxLength: {
                      value: MaxLengthOfUserName,
                      message: getMaxLengthMessage(MaxLengthOfUserName),
                    },
                  })}
                  autoComplete="family-name"
                  className="w-full"
                  placeholder="山田"
                />
              </FormUnit>

              <FormUnit
                id="first_name"
                label="名"
                error={errors.first_name?.message || ""}
                attention="strongRequired"
              >
                <input
                  type="text"
                  {...register("first_name", {
                    required: RequiredInputMessage,
                    maxLength: {
                      value: MaxLengthOfUserName,
                      message: getMaxLengthMessage(MaxLengthOfUserName),
                    },
                  })}
                  autoComplete="given-name"
                  className="w-full"
                  placeholder="太郎"
                />
              </FormUnit>
            </MultiColumnInputBox>

            <Maybe condition={industries.length > 0}>
              <MultiColumnInputBox columns={isSp ? 1 : 2}>
                <FormUnit
                  id="industry_id"
                  label="業種/会社"
                  error={errors.industry_id?.message || ""}
                  attention={"strongRequired"}
                >
                  <SingleSelect
                    name="industry_id"
                    rules={{
                      required: RequiredSelectMessage,
                      valueAsNumber: true,
                      validate: () => {
                        if (!recommendDesigner) {
                          return getProAccountMessage(
                            "業種/会社と職種/担当の組み合わせ",
                          );
                        }
                      },
                    }}
                    options={industries.map(({ label, id }) => ({
                      label,
                      value: id.toString(),
                    }))}
                  />
                </FormUnit>
              </MultiColumnInputBox>
            </Maybe>

            <Maybe condition={occupations.length > 0}>
              <MultiColumnInputBox columns={isSp ? 1 : 2}>
                <FormUnit
                  id="occupation"
                  label="職種/担当"
                  error={errors.occupation_id?.message || ""}
                  attention={"strongRequired"}
                >
                  <SingleSelect
                    name="occupation_id"
                    rules={{
                      required: RequiredSelectMessage,
                      valueAsNumber: true,
                      validate: () => {
                        if (!recommendDesigner) {
                          return getProAccountMessage(
                            "業種/会社と職種/担当の組み合わせ",
                          );
                        }
                      },
                    }}
                    options={occupations.map(({ label, id }) => ({
                      label,
                      value: id.toString(),
                    }))}
                  />
                </FormUnit>
              </MultiColumnInputBox>
            </Maybe>

            <MultiColumnInputBox columns={isSp ? 1 : 2}>
              <FormUnit
                id="birth_year"
                label="生まれた年"
                error={errors.birth_year?.message || ""}
                attention="strongRequired"
              >
                <SingleSelect
                  name="birth_year"
                  rules={{
                    required: RequiredSelectMessage,
                    valueAsNumber: true,
                  }}
                  options={birthYearOptions}
                />
              </FormUnit>
            </MultiColumnInputBox>

            <FormUnit
              id="qualification_ids"
              label="取得資格・免許"
              error={errors.qualification_ids?.message || ""}
              attention="optional"
            >
              <div className="py-8 [&>*]:mr-20 [&>*]:mb-20">
                {qualifications.map(({ id, label }) => (
                  <CheckboxWithLabel<DesignerAccountUpdateParam>
                    key={id}
                    name="qualification_ids"
                    value={id}
                    defaultChecked={
                      user && user.designer
                        ? user.designer.qualifications
                            .map((q) => q.id)
                            .includes(id)
                        : false
                    }
                  >
                    {label}
                  </CheckboxWithLabel>
                ))}
              </div>
            </FormUnit>

            <hr />

            <MultiColumnInputBox>
              <FormUnit
                id="company_name"
                label="会社名"
                error={errors.company_name?.message || ""}
                attention="strongRequired"
              >
                <input
                  className="w-full"
                  type="text"
                  {...register("company_name", {
                    required: RequiredInputMessage,
                    maxLength: {
                      value: MaxLengthOfCompanyName,
                      message: getMaxLengthMessage(MaxLengthOfCompanyName),
                    },
                  })}
                  autoComplete="organization"
                />
              </FormUnit>
            </MultiColumnInputBox>

            <MultiColumnInputBox columns={isSp ? 1 : 2}>
              <FormUnit
                id="company_prefecture_id"
                label="会社所在地"
                error={errors.company_prefecture_id?.message || ""}
                attention="strongRequired"
              >
                <SingleSelect
                  name="company_prefecture_id"
                  rules={{
                    required: RequiredSelectMessage,
                    valueAsNumber: true,
                  }}
                  options={Prefectures.map(({ label, value }) => {
                    return { label, value: String(value) };
                  })}
                />
              </FormUnit>
            </MultiColumnInputBox>

            <MultiColumnInputBox>
              <FormUnit id="branch" label="支店" attention="optional">
                <input
                  className="w-full"
                  type="text"
                  placeholder="東京支店"
                  {...register("branch", {
                    maxLength: {
                      value: MaxLengthOfBranch,
                      message: getMaxLengthMessage(MaxLengthOfBranch),
                    },
                  })}
                />
              </FormUnit>

              <FormUnit id="department" label="部署" attention="optional">
                <input
                  className="w-full"
                  type="text"
                  placeholder="設計部"
                  {...register("department", {
                    maxLength: {
                      value: MaxLengthOfDepartment,
                      message: getMaxLengthMessage(MaxLengthOfDepartment),
                    },
                  })}
                />
              </FormUnit>
            </MultiColumnInputBox>

            <MultiColumnInputBox>
              <FormUnit
                id="official_position"
                label="役職"
                attention="optional"
              >
                <input
                  className="w-full"
                  type="text"
                  {...register("official_position", {
                    maxLength: {
                      value: MaxLengthOfOfficialPosition,
                      message: getMaxLengthMessage(MaxLengthOfOfficialPosition),
                    },
                  })}
                  autoComplete="organization-title"
                  placeholder="部長"
                />
              </FormUnit>
            </MultiColumnInputBox>
          </div>

          <MultiColumnInputBox className="max-w-[400px]">
            <Button type="button" color="neutral" onClick={() => reset()}>
              キャンセル
            </Button>
            <Button type="submit">更新</Button>
          </MultiColumnInputBox>
        </div>
      </form>
    </FormProvider>
  );
};

export default ProAccountForm;
